import React, { useState, useEffect } from 'react';
import { roleService, UserWithRole, Role, Permission } from '../services/roleService';
import {
  adminService,
  NotificationType,
  UserNotificationPreference,
} from '../services/adminService';
import { WithPermission } from '../contexts/PermissionsContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Edit, Person, Lock, Add, Notifications } from '@mui/icons-material';

const EXECUTIVE_DASHBOARD_PERMISSION = 'reports.executive_dashboard';

interface SalesAgentOption {
  id: number;
  name: string;
}

type ManagedUser = UserWithRole & { sales_agent_id?: number | null };

function role_views_dashboard(roles: Role[], role_id: number | null): boolean {
  const role = roles.find((item) => item.id === role_id);
  return !!role?.permissions?.includes(EXECUTIVE_DASHBOARD_PERMISSION);
}

async function put_user_role(
  user_id: number,
  body: {
    role_id: number;
    sales_agent_id: number | null;
    view_executive_dashboard: boolean;
  }
): Promise<void> {
  const token = localStorage.getItem('token');
  const response = await fetch(`/api/admin/users/${user_id}/role`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.error?.message || 'Failed to update user role');
  }
}

const empty_new_user = {
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  role_id: null as number | null,
  sales_agent_id: null as number | null,
  view_executive_dashboard: false,
};

function blank_new_user() {
  return { ...empty_new_user };
}

async function fetch_sales_agents(): Promise<SalesAgentOption[]> {
  const token = localStorage.getItem('token');
  const response = await fetch('/api/admin/sales-agents', {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.error?.message || 'Failed to load sales agents');
  }
  return data.data || [];
}

interface UserManagementProps {
  onUserUpdate?: () => void;
}

export const UserManagement: React.FC<UserManagementProps> = ({ onUserUpdate }) => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [sales_agents, set_sales_agents] = useState<SalesAgentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if current user is Super Admin
  const isSuperAdmin = user && (user.role_id === 1 || user.role_name === 'Super Admin');

  // Filter users based on current user's role
  // Non-Super Admins should not see Super Admin users
  const filteredUsers = isSuperAdmin
    ? users
    : users.filter((u) => u.role_id !== 1 && u.role_name !== 'Super Admin');
  const [selectedUser, setSelectedUser] = useState<ManagedUser | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserData, setNewUserData] = useState(blank_new_user);

  useEffect(() => {
    // Only load data if authenticated and not loading
    // Also wait a bit to ensure auth context has finished initializing
    if (isAuthenticated && !authLoading && user) {
      loadData();
    }
  }, [isAuthenticated, authLoading, user]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load users first (most important)
      const usersData = await roleService.getUsersWithRoles();
      setUsers(usersData as ManagedUser[]);

      // Load roles and permissions in parallel
      // If these fail, we can still show the users list
      try {
        const [rolesData, permissionsData, salesAgentsData] = await Promise.all([
          roleService.getRoles(),
          roleService.getPermissions(),
          fetch_sales_agents(),
        ]);
        setRoles(rolesData);
        setPermissions(permissionsData);
        set_sales_agents(salesAgentsData);
      } catch (err: any) {
        console.error('Failed to load roles/permissions (non-critical):', err);
        // Don't set error for roles/permissions failures - users can still use the page
        // Only set roles/permissions to empty if they fail
        if (!roles.length) setRoles([]);
        if (!permissions.length) setPermissions([]);
      }
    } catch (err: any) {
      console.error('Failed to load user data:', err);
      const errorMessage = err?.message || 'Failed to load user data';
      setError(errorMessage);

      // Don't clear existing data on error - allow graceful degradation
    } finally {
      setLoading(false);
    }
  };

  const handleRoleUpdate = async (
    userId: number,
    roleId: number | null,
    sales_agent_id: number | null,
    view_executive_dashboard: boolean
  ) => {
    try {
      if (!roleId) {
        setError('Role is required');
        return;
      }

      if (roleId === 1) {
        setError('Cannot assign Super Admin role');
        return;
      }

      await put_user_role(userId, {
        role_id: roleId,
        sales_agent_id,
        view_executive_dashboard,
      });
      await loadData();
      onUserUpdate?.();
      setShowRoleModal(false);
      setSelectedUser(null);
      setError(null);
    } catch (err: any) {
      console.error('Failed to update user role:', err);
      throw new Error(err?.message || 'Failed to update user role');
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || !newPassword.trim()) return;

    try {
      await adminService.updateUserPassword(selectedUser.id, { password: newPassword.trim() });
      setShowPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
      setError(null);
      await loadData();
      onUserUpdate?.();
    } catch (err) {
      console.error('Failed to update password:', err);
      setError('Failed to update password');
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords match
    if (newUserData.password !== newUserData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (newUserData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    // Validate role is selected
    if (!newUserData.role_id) {
      setError('Role is required');
      return;
    }

    // Validate Super Admin role is not selected
    if (newUserData.role_id === 1) {
      setError('Cannot assign Super Admin role');
      return;
    }

    try {
      // Determine role based on role_id or default to 'user'
      const role = newUserData.role_id
        ? roles
            .find((r) => r.id === newUserData.role_id)
            ?.name?.toLowerCase()
            .includes('admin')
          ? 'admin'
          : 'user'
        : 'user';

      await adminService.createUser({
        username: newUserData.username,
        email: newUserData.email,
        password: newUserData.password,
        role: role as 'admin' | 'user',
      });

      // Reload users to get the newly created user
      await loadData();

      // Assign role after creation (role_id is required)
      const updatedUsers = await roleService.getUsersWithRoles();
      const createdUser = updatedUsers.find((u) => u.email === newUserData.email);

      if (createdUser && newUserData.role_id) {
        await put_user_role(createdUser.id, {
          role_id: newUserData.role_id,
          sales_agent_id: newUserData.sales_agent_id,
          view_executive_dashboard: newUserData.view_executive_dashboard,
        });
        // Reload again to show the updated role
        await loadData();
      }

      setNewUserData(blank_new_user());
      setShowAddUserModal(false);
      setError(null);
      onUserUpdate?.();
    } catch (err: any) {
      console.error('Failed to create user:', err);
      setError(err.message || 'Failed to create user');
    }
  };

  const getRoleName = (user: UserWithRole): string => {
    return user.role_name || user.role || 'No Role';
  };

  const getRolePermissions = (user: UserWithRole): string[] => {
    return user.permissions || [];
  };

  const getPermissionName = (permissionId: string): string => {
    const permission = permissions.find((p) => p.id === permissionId);
    return permission?.name || permissionId;
  };

  const getPermissionCategory = (permissionId: string): string => {
    const permission = permissions.find((p) => p.id === permissionId);
    return permission?.category || 'unknown';
  };

  const groupPermissionsByCategory = (permissionIds: string[]) => {
    const grouped: { [key: string]: string[] } = {};
    permissionIds.forEach((id) => {
      const category = getPermissionCategory(id);
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(id);
    });
    return grouped;
  };

  if (authLoading || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Alert severity="warning">Please log in to access user management.</Alert>;
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={loadData}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h2">
          User Management
        </Typography>
        <WithPermission permission="admin.user_management">
          <Button variant="contained" startIcon={<Add />} onClick={() => setShowAddUserModal(true)}>
            Add User
          </Button>
        </WithPermission>
      </Box>

      <Card>
        <CardContent>
          <List>
            {filteredUsers.map((user, index) => (
              <React.Fragment key={user.id}>
                <ListItem alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar>
                      <Person />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle1" component="div">
                            {user.username}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                          {user.sales_agent_id != null && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Sales agent:{' '}
                              {sales_agents.find((agent) => agent.id === user.sales_agent_id)?.name ||
                                user.sales_agent_id}
                            </Typography>
                          )}
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body2" fontWeight="medium">
                            {getRoleName(user)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getRolePermissions(user).length} permissions
                          </Typography>
                        </Box>
                      </Box>
                    }
                    secondary={
                      getRolePermissions(user).length > 0 && (
                        <Box mt={2}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            gutterBottom
                          >
                            Permissions:
                          </Typography>
                          <Box>
                            {Object.entries(
                              groupPermissionsByCategory(getRolePermissions(user))
                            ).map(([category, permissionIds]) => (
                              <Box key={category} mb={1}>
                                <Typography variant="caption" fontWeight="medium" display="block">
                                  {category.replace('_', ' ').toUpperCase()}:
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                  {permissionIds.map((permissionId) => (
                                    <Chip
                                      key={permissionId}
                                      label={getPermissionName(permissionId)}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                  <ListItemSecondaryAction>
                    <WithPermission permission="admin.user_management">
                      <IconButton
                        edge="end"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowRoleModal(true);
                        }}
                        color="primary"
                        sx={{ mr: 1 }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowPasswordModal(true);
                        }}
                        color="secondary"
                        sx={{ mr: 1 }}
                      >
                        <Lock />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowNotificationModal(true);
                        }}
                        color="primary"
                      >
                        <Notifications />
                      </IconButton>
                    </WithPermission>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < filteredUsers.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Role Assignment Modal */}
      <RoleAssignmentModal
        open={showRoleModal}
        user={selectedUser}
        roles={roles}
        permissions={permissions}
        onClose={() => {
          setShowRoleModal(false);
          setSelectedUser(null);
        }}
        sales_agents={sales_agents}
        onRoleUpdate={handleRoleUpdate}
      />

      {/* Add User Modal */}
      <Dialog
        open={showAddUserModal}
        onClose={() => {
          setShowAddUserModal(false);
          setNewUserData(blank_new_user());
          setError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New User</DialogTitle>
        <form onSubmit={handleCreateUser}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Username"
              type="text"
              fullWidth
              variant="outlined"
              value={newUserData.username}
              onChange={(e) => setNewUserData({ ...newUserData, username: e.target.value })}
              required
              sx={{ mb: 2 }}
              inputProps={{ minLength: 3, maxLength: 50 }}
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              variant="outlined"
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              variant="outlined"
              value={newUserData.password}
              onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
              required
              helperText="Password must be at least 6 characters long"
              inputProps={{ minLength: 6 }}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Confirm Password"
              type="password"
              fullWidth
              variant="outlined"
              value={newUserData.confirmPassword}
              onChange={(e) => setNewUserData({ ...newUserData, confirmPassword: e.target.value })}
              required
              helperText="Passwords must match"
              inputProps={{ minLength: 6 }}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Role *</InputLabel>
              <Select
                value={newUserData.role_id || ''}
                onChange={(e) => {
                  const role_id = e.target.value ? (parseInt(String(e.target.value)) as number) : null;
                  setNewUserData({
                    ...newUserData,
                    role_id,
                    view_executive_dashboard: role_views_dashboard(roles, role_id),
                  });
                }}
                label="Role *"
                required
              >
                {roles
                  .filter((role) => role.id !== 1 && role.name !== 'Super Admin')
                  .map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <SalesAgentFields
              sales_agents={sales_agents}
              sales_agent_id={newUserData.sales_agent_id}
              view_executive_dashboard={newUserData.view_executive_dashboard}
              on_sales_agent_id={(sales_agent_id) =>
                setNewUserData({ ...newUserData, sales_agent_id })
              }
              on_view_executive_dashboard={(view_executive_dashboard) =>
                setNewUserData({ ...newUserData, view_executive_dashboard })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowAddUserModal(false);
                setNewUserData(blank_new_user());
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Create User
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Password Update Modal */}
      <Dialog
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Password for {selectedUser?.username}</DialogTitle>
        <form onSubmit={handlePasswordUpdate}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="New Password"
              type="password"
              fullWidth
              variant="outlined"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              helperText="Password must be at least 6 characters long"
              inputProps={{ minLength: 6 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowPasswordModal(false);
                setSelectedUser(null);
                setNewPassword('');
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Update Password
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Notification Preferences Modal */}
      <NotificationPreferencesModal
        open={showNotificationModal}
        user={selectedUser}
        onClose={() => {
          setShowNotificationModal(false);
          setSelectedUser(null);
        }}
      />
    </Box>
  );
};

function SalesAgentFields({
  sales_agents,
  sales_agent_id,
  view_executive_dashboard,
  on_sales_agent_id,
  on_view_executive_dashboard,
}: {
  sales_agents: SalesAgentOption[];
  sales_agent_id: number | null;
  view_executive_dashboard: boolean;
  on_sales_agent_id: (sales_agent_id: number | null) => void;
  on_view_executive_dashboard: (view_executive_dashboard: boolean) => void;
}) {
  return (
    <>
      <FormControl fullWidth margin="normal">
        <InputLabel>Sales agent</InputLabel>
        <Select
          value={sales_agent_id ?? ''}
          label="Sales agent"
          onChange={(e) =>
            on_sales_agent_id(e.target.value === '' ? null : Number(e.target.value))
          }
        >
          <MenuItem value="">None</MenuItem>
          {sales_agents.map((agent) => (
            <MenuItem key={agent.id} value={agent.id}>
              {agent.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox
            checked={view_executive_dashboard}
            onChange={(e) => on_view_executive_dashboard(e.target.checked)}
          />
        }
        label="View executive dashboard"
      />
      <Typography variant="caption" color="text.secondary" display="block">
        Applies to this user only. Checked: they can open the executive dashboard. Unchecked: they
        cannot open that page. A linked sales agent sees only their own sales.
      </Typography>
    </>
  );
}

interface RoleAssignmentModalProps {
  open: boolean;
  user: ManagedUser | null;
  roles: Role[];
  permissions: Permission[];
  sales_agents: SalesAgentOption[];
  onClose: () => void;
  onRoleUpdate: (
    userId: number,
    roleId: number | null,
    sales_agent_id: number | null,
    view_executive_dashboard: boolean
  ) => Promise<void>;
}

const RoleAssignmentModal: React.FC<RoleAssignmentModalProps> = ({
  open,
  user,
  roles,
  permissions,
  sales_agents,
  onClose,
  onRoleUpdate,
}) => {
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(user?.role_id || null);
  const [sales_agent_id, set_sales_agent_id] = useState<number | null>(user?.sales_agent_id ?? null);
  const [view_executive_dashboard, set_view_executive_dashboard] = useState(
    !!user?.permissions?.includes(EXECUTIVE_DASHBOARD_PERMISSION)
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedRoleId(user?.role_id || null);
    set_sales_agent_id(user?.sales_agent_id ?? null);
    set_view_executive_dashboard(!!user?.permissions?.includes(EXECUTIVE_DASHBOARD_PERMISSION));
    setError(null);
  }, [user, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Validate role is selected
    if (!selectedRoleId) {
      setError('Role is required');
      return;
    }

    // Validate Super Admin role is not selected
    if (selectedRoleId === 1) {
      setError('Cannot assign Super Admin role');
      return;
    }

    setError(null);
    try {
      await onRoleUpdate(user.id, selectedRoleId, sales_agent_id, view_executive_dashboard);
    } catch (err: any) {
      setError(err?.message || 'Failed to update user role');
    }
  };

  const selectedRole = roles.find((r) => r.id === selectedRoleId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Role to {user?.username}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Select Role *</InputLabel>
            <Select
              value={selectedRoleId || ''}
              onChange={(e) => {
                const role_id = e.target.value ? parseInt(String(e.target.value)) : null;
                setSelectedRoleId(role_id);
                set_view_executive_dashboard(role_views_dashboard(roles, role_id));
              }}
              label="Select Role *"
              required
            >
              {roles
                .filter((role) => role.id !== 1 && role.name !== 'Super Admin')
                .map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <SalesAgentFields
            sales_agents={sales_agents}
            sales_agent_id={sales_agent_id}
            view_executive_dashboard={view_executive_dashboard}
            on_sales_agent_id={set_sales_agent_id}
            on_view_executive_dashboard={set_view_executive_dashboard}
          />

          {selectedRole && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Role Permissions:
              </Typography>
              <Box
                maxHeight="200px"
                overflow="auto"
                border="1px solid #e0e0e0"
                borderRadius={1}
                p={1}
              >
                {selectedRole.permissions
                  .filter((permissionId) => permissionId !== 'reports.my_sales')
                  .map((permissionId) => (
                  <Typography key={permissionId} variant="body2" sx={{ mb: 0.5 }}>
                    • {permissions.find((item) => item.id === permissionId)?.name || permissionId}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Update Role
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface NotificationPreferencesModalProps {
  open: boolean;
  user: UserWithRole | null;
  onClose: () => void;
}

const NotificationPreferencesModal: React.FC<NotificationPreferencesModalProps> = ({
  open,
  user,
  onClose,
}) => {
  const [notificationTypes, setNotificationTypes] = useState<NotificationType[]>([]);
  const [userPreferences, setUserPreferences] = useState<UserNotificationPreference[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState<string | null>(null);

  // Load notification types and user preferences when modal opens
  useEffect(() => {
    if (open && user) {
      loadNotificationData();
    } else {
      // Reset state when modal closes
      setNotificationTypes([]);
      setUserPreferences([]);
      setError(null);
    }
  }, [open, user]);

  const loadNotificationData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const [types, preferences] = await Promise.all([
        adminService.getNotificationTypes(),
        adminService.getUserNotificationPreferences(user.id),
      ]);

      setNotificationTypes(types);
      setUserPreferences(preferences);
    } catch (err: any) {
      console.error('Failed to load notification data:', err);
      setError(err.message || 'Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const isNotificationEnabled = (notificationType: string): boolean => {
    const preference = userPreferences.find((p) => p.notification_type === notificationType);
    return preference ? preference.email_enabled && preference.frequency !== 'never' : false;
  };

  const bloomStockFrequency = (): 'immediate' | 'daily' => {
    const preference = userPreferences.find((p) => p.notification_type === 'bulk_low_stock');
    return preference?.frequency === 'daily' ? 'daily' : 'immediate';
  };

  const saveNotificationPreference = async (
    notificationType: string,
    updates: { email_enabled: boolean; in_app_enabled: boolean; frequency: 'immediate' | 'daily' | 'weekly' | 'never' }
  ) => {
    if (!user) return;
    const updatedPreference = await adminService.updateUserNotificationPreference(
      user.id,
      notificationType,
      updates
    );
    const existingPreference = userPreferences.find((p) => p.notification_type === notificationType);
    if (existingPreference) {
      setUserPreferences(
        userPreferences.map((p) =>
          p.notification_type === notificationType ? updatedPreference : p
        )
      );
    } else {
      setUserPreferences([...userPreferences, updatedPreference]);
    }
  };

  const handleToggleNotification = async (notificationType: string, enabled: boolean) => {
    if (!user) return;

    try {
      setSaving(true);
      setError(null);

      // Determine frequency based on notification type
      const notificationTypeInfo = notificationTypes.find((nt) => nt.type === notificationType);
      const frequency =
        notificationType === 'bulk_low_stock'
          ? bloomStockFrequency()
          : ((notificationTypeInfo?.default_frequency as 'immediate' | 'daily' | 'weekly' | 'never') ||
            'daily');

      await saveNotificationPreference(notificationType, {
        email_enabled: enabled,
        in_app_enabled: enabled,
        frequency: enabled ? frequency : 'never',
      });
    } catch (err: any) {
      console.error('Failed to update notification preference:', err);
      setError(err.message || 'Failed to update notification preference');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async (notificationType: string) => {
    if (!user) return;

    try {
      setSendingTestEmail(notificationType);
      setError(null);

      const result = await adminService.sendTestNotificationEmail(user.id, notificationType);

      if (result.emailSent) {
        // Show success message - you might want to use a snackbar instead
        alert(`Test email sent successfully to ${result.recipient}`);
      } else {
        setError('Failed to send test email. Please check email service configuration.');
      }
    } catch (err: any) {
      console.error('Failed to send test email:', err);
      setError(err.message || 'Failed to send test email');
    } finally {
      setSendingTestEmail(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Notification Preferences for {user?.username}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select which notifications this user should receive. Users will receive email
              notifications for enabled items.
            </Typography>
            <Box>
              {notificationTypes.map((notificationType) => {
                const enabled = isNotificationEnabled(notificationType.type);
                return (
                  <Box
                    key={notificationType.type}
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={enabled}
                            onChange={(e) =>
                              handleToggleNotification(notificationType.type, e.target.checked)
                            }
                            disabled={saving}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {notificationType.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {notificationType.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {notificationType.type === 'bulk_low_stock' && enabled
                                ? `Immediate: email when an item becomes low. Daily: morning list of items still low.`
                                : `Frequency: ${notificationType.default_frequency}`}
                              {' • '}Severity: {notificationType.severity}
                            </Typography>
                            {notificationType.type === 'bulk_low_stock' && enabled && (
                              <FormControl size="small" sx={{ mt: 1, minWidth: 160 }}>
                                <InputLabel id="bloom-stock-frequency-label">Frequency</InputLabel>
                                <Select
                                  labelId="bloom-stock-frequency-label"
                                  label="Frequency"
                                  value={bloomStockFrequency()}
                                  disabled={saving}
                                  onChange={async (e) => {
                                    if (!user) return;
                                    try {
                                      setSaving(true);
                                      setError(null);
                                      await saveNotificationPreference('bulk_low_stock', {
                                        email_enabled: true,
                                        in_app_enabled: true,
                                        frequency: e.target.value as 'immediate' | 'daily',
                                      });
                                    } catch (err: any) {
                                      setError(err.message || 'Failed to update notification preference');
                                    } finally {
                                      setSaving(false);
                                    }
                                  }}
                                >
                                  <MenuItem value="immediate">Immediate</MenuItem>
                                  <MenuItem value="daily">Daily</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                          </Box>
                        }
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleSendTestEmail(notificationType.type)}
                        disabled={saving || sendingTestEmail === notificationType.type}
                        sx={{ ml: 2, minWidth: 140 }}
                      >
                        {sendingTestEmail === notificationType.type ? (
                          <>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Sending...
                          </>
                        ) : (
                          'Send Test Email'
                        )}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
